/**
 * @module usePassenger
 */
import { useCallback } from 'react'
import {
  useGetPassengersMutation,
  useGetPassengerMutation,
  useGetPassengerByEmailMutation,
  useCreatePassengerMutation,
  useUpdatePassengerMutation,
  useDeletePassengerMutation,
  useUpdatePassengerStatusMutation,
  useUpdatePassportMutation,
  useUpdateContactInfoMutation,
  useUpdateLoyaltyProgramMutation,
  useAddLoyaltyPointsMutation,
  useRedeemLoyaltyPointsMutation,
  useUpgradeLoyaltyTierMutation,
  useAddBookingMutation,
  useUpdateBookingMutation,
  useAddPaymentMethodMutation,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useAddTravelDocumentMutation,
  useRemoveTravelDocumentMutation,
  useUpdateSpecialRequirementsMutation,
  useAddSpecialAssistanceMutation,
  useRemoveSpecialAssistanceMutation,
  useAddMedicalConditionMutation,
  useRemoveMedicalConditionMutation,
  useAddDietaryRestrictionMutation,
  useRemoveDietaryRestrictionMutation,
  useUpdateMobilityAssistanceMutation,
  useUpdateCommunicationPreferencesMutation,
  useGetPassengersByAirlineMutation,
} from '../services/passenger.service'

export const usePassenger = () => {
  // Basic CRUD operations
  const [executeGetPassengers, { isLoading: isGetPassengersLoading }] = useGetPassengersMutation()
  const [executeGetPassenger, { isLoading: isGetPassengerLoading }] = useGetPassengerMutation()
  const [executeGetPassengerByEmail, { isLoading: isGetPassengerByEmailLoading }] =
    useGetPassengerByEmailMutation()
  const [executeCreatePassenger, { isLoading: isCreatePassengerLoading }] =
    useCreatePassengerMutation()
  const [executeUpdatePassenger, { isLoading: isUpdatePassengerLoading }] =
    useUpdatePassengerMutation()
  const [executeDeletePassenger, { isLoading: isDeletePassengerLoading }] =
    useDeletePassengerMutation()

  // Status and profile updates
  const [executeUpdatePassengerStatus, { isLoading: isUpdatePassengerStatusLoading }] =
    useUpdatePassengerStatusMutation()
  const [executeUpdatePassport, { isLoading: isUpdatePassportLoading }] =
    useUpdatePassportMutation()
  const [executeUpdateContactInfo, { isLoading: isUpdateContactInfoLoading }] =
    useUpdateContactInfoMutation()

  // Loyalty program operations
  const [executeUpdateLoyaltyProgram, { isLoading: isUpdateLoyaltyProgramLoading }] =
    useUpdateLoyaltyProgramMutation()
  const [executeAddLoyaltyPoints, { isLoading: isAddLoyaltyPointsLoading }] =
    useAddLoyaltyPointsMutation()
  const [executeRedeemLoyaltyPoints, { isLoading: isRedeemLoyaltyPointsLoading }] =
    useRedeemLoyaltyPointsMutation()
  const [executeUpgradeLoyaltyTier, { isLoading: isUpgradeLoyaltyTierLoading }] =
    useUpgradeLoyaltyTierMutation()

  // Booking operations
  const [executeAddBooking, { isLoading: isAddBookingLoading }] = useAddBookingMutation()
  const [executeUpdateBooking, { isLoading: isUpdateBookingLoading }] = useUpdateBookingMutation()

  // Payment method operations
  const [executeAddPaymentMethod, { isLoading: isAddPaymentMethodLoading }] =
    useAddPaymentMethodMutation()
  const [executeRemovePaymentMethod, { isLoading: isRemovePaymentMethodLoading }] =
    useRemovePaymentMethodMutation()
  const [executeSetDefaultPaymentMethod, { isLoading: isSetDefaultPaymentMethodLoading }] =
    useSetDefaultPaymentMethodMutation()

  // Travel document operations
  const [executeAddTravelDocument, { isLoading: isAddTravelDocumentLoading }] =
    useAddTravelDocumentMutation()
  const [executeRemoveTravelDocument, { isLoading: isRemoveTravelDocumentLoading }] =
    useRemoveTravelDocumentMutation()

  // Special requirements operations
  const [executeUpdateSpecialRequirements, { isLoading: isUpdateSpecialRequirementsLoading }] =
    useUpdateSpecialRequirementsMutation()
  const [executeAddSpecialAssistance, { isLoading: isAddSpecialAssistanceLoading }] =
    useAddSpecialAssistanceMutation()
  const [executeRemoveSpecialAssistance, { isLoading: isRemoveSpecialAssistanceLoading }] =
    useRemoveSpecialAssistanceMutation()

  // Medical and dietary operations
  const [executeAddMedicalCondition, { isLoading: isAddMedicalConditionLoading }] =
    useAddMedicalConditionMutation()
  const [executeRemoveMedicalCondition, { isLoading: isRemoveMedicalConditionLoading }] =
    useRemoveMedicalConditionMutation()
  const [executeAddDietaryRestriction, { isLoading: isAddDietaryRestrictionLoading }] =
    useAddDietaryRestrictionMutation()
  const [executeRemoveDietaryRestriction, { isLoading: isRemoveDietaryRestrictionLoading }] =
    useRemoveDietaryRestrictionMutation()

  // Mobility assistance
  const [executeUpdateMobilityAssistance, { isLoading: isUpdateMobilityAssistanceLoading }] =
    useUpdateMobilityAssistanceMutation()

  // Communication preferences
  const [
    executeUpdateCommunicationPreferences,
    { isLoading: isUpdateCommunicationPreferencesLoading },
  ] = useUpdateCommunicationPreferencesMutation()

  // Airline-related operations
  const [executeGetPassengersByAirline, { isLoading: isGetPassengersByAirlineLoading }] =
    useGetPassengersByAirlineMutation()

  // Basic CRUD async functions
  const getPassengersAsync = useCallback(async () => {
    if (isGetPassengersLoading) {
      return
    }
    try {
      const response = await executeGetPassengers().unwrap()
      return response.data.passengers
    } catch (error) {
      console.error('#getPassengersAsync: Error fetching passengers', error)
      throw error
    }
  }, [executeGetPassengers, isGetPassengersLoading])

  const getPassengerAsync = useCallback(
    async (passengerId) => {
      if (isGetPassengerLoading) {
        return
      }
      try {
        const response = await executeGetPassenger(passengerId).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#getPassengerAsync: Error fetching passenger', error)
        throw error
      }
    },
    [executeGetPassenger, isGetPassengerLoading],
  )

  const getPassengerByEmailAsync = useCallback(
    async (email) => {
      if (isGetPassengerByEmailLoading) {
        return
      }
      try {
        const response = await executeGetPassengerByEmail(email).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#getPassengerByEmailAsync: Error fetching passenger by email', error)
        throw error
      }
    },
    [executeGetPassengerByEmail, isGetPassengerByEmailLoading],
  )

  const createPassengerAsync = useCallback(
    async (passenger) => {
      if (isCreatePassengerLoading) {
        return
      }
      try {
        const response = await executeCreatePassenger(passenger).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#createPassengerAsync: Error creating passenger', error)
        throw error
      }
    },
    [executeCreatePassenger, isCreatePassengerLoading],
  )

  const updatePassengerAsync = useCallback(
    async (passengerId, updates) => {
      if (isUpdatePassengerLoading) {
        return
      }
      try {
        const response = await executeUpdatePassenger({ passengerId, ...updates }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updatePassengerAsync: Error updating passenger', error)
        throw error
      }
    },
    [executeUpdatePassenger, isUpdatePassengerLoading],
  )

  const deletePassengerAsync = useCallback(
    async (passengerId) => {
      if (isDeletePassengerLoading) {
        return
      }
      try {
        const response = await executeDeletePassenger(passengerId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deletePassengerAsync: Error deleting passenger', error)
        throw error
      }
    },
    [executeDeletePassenger, isDeletePassengerLoading],
  )

  // Status and profile async functions
  const updatePassengerStatusAsync = useCallback(
    async (passengerId, status) => {
      if (isUpdatePassengerStatusLoading) {
        return
      }
      try {
        const response = await executeUpdatePassengerStatus({ passengerId, status }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updatePassengerStatusAsync: Error updating passenger status', error)
        throw error
      }
    },
    [executeUpdatePassengerStatus, isUpdatePassengerStatusLoading],
  )

  const updatePassportAsync = useCallback(
    async (passengerId, passport) => {
      if (isUpdatePassportLoading) {
        return
      }
      try {
        const response = await executeUpdatePassport({ passengerId, passport }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updatePassportAsync: Error updating passport', error)
        throw error
      }
    },
    [executeUpdatePassport, isUpdatePassportLoading],
  )

  const updateContactInfoAsync = useCallback(
    async (passengerId, contactInfo) => {
      if (isUpdateContactInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateContactInfo({ passengerId, contactInfo }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updateContactInfoAsync: Error updating contact info', error)
        throw error
      }
    },
    [executeUpdateContactInfo, isUpdateContactInfoLoading],
  )

  // Loyalty program async functions
  const updateLoyaltyProgramAsync = useCallback(
    async (passengerId, loyaltyProgram) => {
      if (isUpdateLoyaltyProgramLoading) {
        return
      }
      try {
        const response = await executeUpdateLoyaltyProgram({ passengerId, loyaltyProgram }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updateLoyaltyProgramAsync: Error updating loyalty program', error)
        throw error
      }
    },
    [executeUpdateLoyaltyProgram, isUpdateLoyaltyProgramLoading],
  )

  const addLoyaltyPointsAsync = useCallback(
    async (passengerId, points) => {
      if (isAddLoyaltyPointsLoading) {
        return
      }
      try {
        const response = await executeAddLoyaltyPoints({ passengerId, points }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addLoyaltyPointsAsync: Error adding loyalty points', error)
        throw error
      }
    },
    [executeAddLoyaltyPoints, isAddLoyaltyPointsLoading],
  )

  const redeemLoyaltyPointsAsync = useCallback(
    async (passengerId, points) => {
      if (isRedeemLoyaltyPointsLoading) {
        return
      }
      try {
        const response = await executeRedeemLoyaltyPoints({ passengerId, points }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#redeemLoyaltyPointsAsync: Error redeeming loyalty points', error)
        throw error
      }
    },
    [executeRedeemLoyaltyPoints, isRedeemLoyaltyPointsLoading],
  )

  const upgradeLoyaltyTierAsync = useCallback(
    async (passengerId, tier) => {
      if (isUpgradeLoyaltyTierLoading) {
        return
      }
      try {
        const response = await executeUpgradeLoyaltyTier({ passengerId, tier }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#upgradeLoyaltyTierAsync: Error upgrading loyalty tier', error)
        throw error
      }
    },
    [executeUpgradeLoyaltyTier, isUpgradeLoyaltyTierLoading],
  )

  // Booking async functions
  const addBookingAsync = useCallback(
    async (passengerId, booking) => {
      if (isAddBookingLoading) {
        return
      }
      try {
        const response = await executeAddBooking({ passengerId, booking }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addBookingAsync: Error adding booking', error)
        throw error
      }
    },
    [executeAddBooking, isAddBookingLoading],
  )

  const updateBookingAsync = useCallback(
    async (passengerId, bookingReference, updates) => {
      if (isUpdateBookingLoading) {
        return
      }
      try {
        const response = await executeUpdateBooking({
          passengerId,
          bookingReference,
          updates,
        }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updateBookingAsync: Error updating booking', error)
        throw error
      }
    },
    [executeUpdateBooking, isUpdateBookingLoading],
  )

  // Payment method async functions
  const addPaymentMethodAsync = useCallback(
    async (passengerId, paymentMethod) => {
      if (isAddPaymentMethodLoading) {
        return
      }
      try {
        const response = await executeAddPaymentMethod({ passengerId, paymentMethod }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addPaymentMethodAsync: Error adding payment method', error)
        throw error
      }
    },
    [executeAddPaymentMethod, isAddPaymentMethodLoading],
  )

  const removePaymentMethodAsync = useCallback(
    async (passengerId, lastFourDigits) => {
      if (isRemovePaymentMethodLoading) {
        return
      }
      try {
        const response = await executeRemovePaymentMethod({ passengerId, lastFourDigits }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#removePaymentMethodAsync: Error removing payment method', error)
        throw error
      }
    },
    [executeRemovePaymentMethod, isRemovePaymentMethodLoading],
  )

  const setDefaultPaymentMethodAsync = useCallback(
    async (passengerId, lastFourDigits) => {
      if (isSetDefaultPaymentMethodLoading) {
        return
      }
      try {
        const response = await executeSetDefaultPaymentMethod({
          passengerId,
          lastFourDigits,
        }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#setDefaultPaymentMethodAsync: Error setting default payment method', error)
        throw error
      }
    },
    [executeSetDefaultPaymentMethod, isSetDefaultPaymentMethodLoading],
  )

  // Travel document async functions
  const addTravelDocumentAsync = useCallback(
    async (passengerId, travelDocument) => {
      if (isAddTravelDocumentLoading) {
        return
      }
      try {
        const response = await executeAddTravelDocument({ passengerId, travelDocument }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addTravelDocumentAsync: Error adding travel document', error)
        throw error
      }
    },
    [executeAddTravelDocument, isAddTravelDocumentLoading],
  )

  const removeTravelDocumentAsync = useCallback(
    async (passengerId, documentNumber) => {
      if (isRemoveTravelDocumentLoading) {
        return
      }
      try {
        const response = await executeRemoveTravelDocument({ passengerId, documentNumber }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#removeTravelDocumentAsync: Error removing travel document', error)
        throw error
      }
    },
    [executeRemoveTravelDocument, isRemoveTravelDocumentLoading],
  )

  // Special requirements async functions
  const updateSpecialRequirementsAsync = useCallback(
    async (passengerId, specialRequirements) => {
      if (isUpdateSpecialRequirementsLoading) {
        return
      }
      try {
        const response = await executeUpdateSpecialRequirements({
          passengerId,
          specialRequirements,
        }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updateSpecialRequirementsAsync: Error updating special requirements', error)
        throw error
      }
    },
    [executeUpdateSpecialRequirements, isUpdateSpecialRequirementsLoading],
  )

  const addSpecialAssistanceAsync = useCallback(
    async (passengerId, assistance) => {
      if (isAddSpecialAssistanceLoading) {
        return
      }
      try {
        const response = await executeAddSpecialAssistance({ passengerId, assistance }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addSpecialAssistanceAsync: Error adding special assistance', error)
        throw error
      }
    },
    [executeAddSpecialAssistance, isAddSpecialAssistanceLoading],
  )

  const removeSpecialAssistanceAsync = useCallback(
    async (passengerId, assistance) => {
      if (isRemoveSpecialAssistanceLoading) {
        return
      }
      try {
        const response = await executeRemoveSpecialAssistance({ passengerId, assistance }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#removeSpecialAssistanceAsync: Error removing special assistance', error)
        throw error
      }
    },
    [executeRemoveSpecialAssistance, isRemoveSpecialAssistanceLoading],
  )

  // Medical and dietary async functions
  const addMedicalConditionAsync = useCallback(
    async (passengerId, condition) => {
      if (isAddMedicalConditionLoading) {
        return
      }
      try {
        const response = await executeAddMedicalCondition({ passengerId, condition }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addMedicalConditionAsync: Error adding medical condition', error)
        throw error
      }
    },
    [executeAddMedicalCondition, isAddMedicalConditionLoading],
  )

  const removeMedicalConditionAsync = useCallback(
    async (passengerId, condition) => {
      if (isRemoveMedicalConditionLoading) {
        return
      }
      try {
        const response = await executeRemoveMedicalCondition({ passengerId, condition }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#removeMedicalConditionAsync: Error removing medical condition', error)
        throw error
      }
    },
    [executeRemoveMedicalCondition, isRemoveMedicalConditionLoading],
  )

  const addDietaryRestrictionAsync = useCallback(
    async (passengerId, type) => {
      if (isAddDietaryRestrictionLoading) {
        return
      }
      try {
        const response = await executeAddDietaryRestriction({ passengerId, type }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#addDietaryRestrictionAsync: Error adding dietary restriction', error)
        throw error
      }
    },
    [executeAddDietaryRestriction, isAddDietaryRestrictionLoading],
  )

  const removeDietaryRestrictionAsync = useCallback(
    async (passengerId, type) => {
      if (isRemoveDietaryRestrictionLoading) {
        return
      }
      try {
        const response = await executeRemoveDietaryRestriction({ passengerId, type }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#removeDietaryRestrictionAsync: Error removing dietary restriction', error)
        throw error
      }
    },
    [executeRemoveDietaryRestriction, isRemoveDietaryRestrictionLoading],
  )

  // Mobility assistance async functions
  const updateMobilityAssistanceAsync = useCallback(
    async (passengerId, mobilityAssistance) => {
      if (isUpdateMobilityAssistanceLoading) {
        return
      }
      try {
        const response = await executeUpdateMobilityAssistance({
          passengerId,
          mobilityAssistance,
        }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error('#updateMobilityAssistanceAsync: Error updating mobility assistance', error)
        throw error
      }
    },
    [executeUpdateMobilityAssistance, isUpdateMobilityAssistanceLoading],
  )

  // Communication preferences async functions
  const updateCommunicationPreferencesAsync = useCallback(
    async (passengerId, communicationPreferences) => {
      if (isUpdateCommunicationPreferencesLoading) {
        return
      }
      try {
        const response = await executeUpdateCommunicationPreferences({
          passengerId,
          communicationPreferences,
        }).unwrap()
        return response.data.passenger
      } catch (error) {
        console.error(
          '#updateCommunicationPreferencesAsync: Error updating communication preferences',
          error,
        )
        throw error
      }
    },
    [executeUpdateCommunicationPreferences, isUpdateCommunicationPreferencesLoading],
  )

  // Airline-related async functions
  const getPassengersByAirlineAsync = useCallback(
    async (airlineId) => {
      if (isGetPassengersByAirlineLoading) {
        return
      }
      try {
        const response = await executeGetPassengersByAirline(airlineId).unwrap()
        return response.data.passengers
      } catch (error) {
        console.error('#getPassengersByAirlineAsync: Error fetching passengers by airline', error)
        throw error
      }
    },
    [executeGetPassengersByAirline, isGetPassengersByAirlineLoading],
  )

  return {
    // Basic CRUD functions
    getPassengersAsync,
    getPassengerAsync,
    getPassengerByEmailAsync,
    createPassengerAsync,
    updatePassengerAsync,
    deletePassengerAsync,

    // Status and profile functions
    updatePassengerStatusAsync,
    updatePassportAsync,
    updateContactInfoAsync,

    // Loyalty program functions
    updateLoyaltyProgramAsync,
    addLoyaltyPointsAsync,
    redeemLoyaltyPointsAsync,
    upgradeLoyaltyTierAsync,

    // Booking functions
    addBookingAsync,
    updateBookingAsync,

    // Payment method functions
    addPaymentMethodAsync,
    removePaymentMethodAsync,
    setDefaultPaymentMethodAsync,

    // Travel document functions
    addTravelDocumentAsync,
    removeTravelDocumentAsync,

    // Special requirements functions
    updateSpecialRequirementsAsync,
    addSpecialAssistanceAsync,
    removeSpecialAssistanceAsync,

    // Medical and dietary functions
    addMedicalConditionAsync,
    removeMedicalConditionAsync,
    addDietaryRestrictionAsync,
    removeDietaryRestrictionAsync,

    // Mobility assistance functions
    updateMobilityAssistanceAsync,

    // Communication preferences functions
    updateCommunicationPreferencesAsync,

    // Airline-related functions
    getPassengersByAirlineAsync,

    // Loading states
    isGetPassengersLoading,
    isGetPassengerLoading,
    isGetPassengerByEmailLoading,
    isCreatePassengerLoading,
    isUpdatePassengerLoading,
    isDeletePassengerLoading,
    isUpdatePassengerStatusLoading,
    isUpdatePassportLoading,
    isUpdateContactInfoLoading,
    isUpdateLoyaltyProgramLoading,
    isAddLoyaltyPointsLoading,
    isRedeemLoyaltyPointsLoading,
    isUpgradeLoyaltyTierLoading,
    isAddBookingLoading,
    isUpdateBookingLoading,
    isAddPaymentMethodLoading,
    isRemovePaymentMethodLoading,
    isSetDefaultPaymentMethodLoading,
    isAddTravelDocumentLoading,
    isRemoveTravelDocumentLoading,
    isUpdateSpecialRequirementsLoading,
    isAddSpecialAssistanceLoading,
    isRemoveSpecialAssistanceLoading,
    isAddMedicalConditionLoading,
    isRemoveMedicalConditionLoading,
    isAddDietaryRestrictionLoading,
    isRemoveDietaryRestrictionLoading,
    isUpdateMobilityAssistanceLoading,
    isUpdateCommunicationPreferencesLoading,
    isGetPassengersByAirlineLoading,
  }
}
